import {contextRangesForMarkers} from './context-ranges-for-markers'
import {Deque} from './deque'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {scheduleLowPriorityUpdate, type CancelHandle} from '@github-ui/schedule-low-priority-update'
import type {ProgressiveDiffsSlice} from '../stores/slices/progressive-diffs'
import type {DiffEntriesSlice} from '../stores/slices/diff-entries'
import type {DiffSummariesSlice} from '../stores/slices/diff-summaries'

export type ProgressiveDiffLoader = {
  // Starts the queue processing loop to load diff entries, optionally background auto loading all difs
  startQueue: (options?: {autoLoadAllDiffs: boolean}) => void
  // Loads a single diff entry with high priority by adding it to the front of the queue
  loadPriority: (pathDigest?: string) => void
  // Loads multiple diff entries with high priority by adding them to the front of the queue
  loadManyPriority: (pathDigests: string[]) => void
  // Re-runs the queue after state it reads, such as the selected digest, changes
  resume: () => void
}

export const makeProgressiveDiffLoader = ({
  get,
  initialLoadedPathDigests,
  getIsComparisonOutdated,
}: {
  get: () => ProgressiveDiffsSlice & DiffEntriesSlice & DiffSummariesSlice
  initialLoadedPathDigests: Set<string>
  getIsComparisonOutdated?: () => boolean
}) => {
  // Track which digests to load. Digests are de-queued from the front of the line
  // Important digests are placed in the front of the line
  // All other digests queue at the end of the line
  const digestsToLoad = new Deque<string>()

  // All digests which have been loaded by this loader
  const digestsLoaded = new Set(initialLoadedPathDigests)

  // All digests which are in flight
  const digestsLoading = new Set<string>()

  // For most operations, the loader will continually fetch digests until all are loaded
  // exceptions: virtual and single file views
  let autoLoadAllDiffs = false

  let queueRunning = false

  // By default, only fetch 4 files per network request
  // Exception: single file and virtual mode
  const DEFAULT_ITEMS_TO_LOAD = 4
  let lengthOfItemsToLoad = DEFAULT_ITEMS_TO_LOAD

  // When enabled, the batch size is floored at the default and always reset after each
  // batch, so a temporarily elevated chunk size never persists across subsequent batches.
  // Evaluated lazily on first use: the loader is constructed synchronously during store creation, which
  // can run in SSR where client env is not yet loaded, so reading the flag eagerly here would throw.
  let batchingRefinementEnabled: boolean | undefined
  const isBatchingRefinementEnabled = () => {
    batchingRefinementEnabled ??= isFeatureEnabled('pull_request_virtualization_loader_batching')
    return batchingRefinementEnabled
  }

  // When enabled, an empty queue stops instead of re-polling. Evaluated lazily for the same reason as above.
  let skipEmptyPollEnabled: boolean | undefined
  const isSkipEmptyPollEnabled = () => {
    skipEmptyPollEnabled ??= isFeatureEnabled('pull_request_diff_loader_skip_empty_poll')
    return skipEmptyPollEnabled
  }

  // Track the current scheduled update so it can be cancelled for debouncing
  let pendingUpdate: CancelHandle | null = null

  const debouncedProcessQueue = () => {
    // Cancel any pending scheduled update before scheduling a new one
    pendingUpdate?.cancel()
    pendingUpdate = scheduleLowPriorityUpdate(processQueue, {timeout: 50})
  }

  // A loading loop which continually loads files until all files are loaded
  const processQueue = async () => {
    if (!queueRunning) return

    const {entriesMap, entriesOrder, selectedPathDigest} = get()

    if (entriesOrder.length === digestsLoaded.size) {
      // 🎉 all diffs loaded, stop queue
      queueRunning = false
      return
    }

    // Auto-load only picks digests from the selection when nothing is queued
    const skippedAutoLoad = autoLoadAllDiffs && digestsToLoad.size > 0

    // Find the next files to load based on the current selected digest
    // All files after the digest will be auto loaded
    if (autoLoadAllDiffs && digestsToLoad.size === 0) {
      const nextPathDigestToLoad = selectedPathDigest
        ? entriesOrder.indexOf(selectedPathDigest)
        : entriesOrder.findIndex((pathDigest: string) => !digestsLoaded.has(pathDigest))

      if (nextPathDigestToLoad > 0) {
        for (let i = nextPathDigestToLoad; i < entriesOrder.length; i++) {
          const pathDigest = entriesOrder[i]
          if (!pathDigest) break

          if (!digestsLoaded.has(pathDigest) && !digestsLoading.has(pathDigest)) {
            digestsToLoad.pushBack(pathDigest)
          }

          if (digestsToLoad.size >= lengthOfItemsToLoad) {
            break
          }
        }
      }
    }

    // What paths to load in this loop. Today the API only accepts paths, but it should actually accept a path digest
    const paths: string[] = []

    // load up the next batch of diffs to load. Stop when:
    // 1. no more digests in the queue
    // 2. diff chunk size it full
    while (digestsToLoad.size > 0 && paths.length < lengthOfItemsToLoad) {
      const pathDigest = digestsToLoad.popFront()
      if (!pathDigest || digestsLoaded.has(pathDigest) || digestsLoading.has(pathDigest)) {
        continue
      }
      const path = entriesMap[pathDigest]?.path
      if (path) {
        paths.push(path)
        digestsLoading.add(pathDigest)
      }
    }

    if (!paths.length) {
      if (!isSkipEmptyPollEnabled()) {
        debouncedProcessQueue()
        return
      }
      // New work only arrives through startQueue/loadPriority/loadManyPriority/resume, and a resolved
      // batch reschedules itself, so re-polling an empty queue could only spin. The exception is queued
      // digests that were all already loaded or loading: the queue is now empty, so one more run lets
      // auto-load pick up from the selection. That run can't skip auto-load, so it can't requeue again.
      if (skippedAutoLoad) debouncedProcessQueue()
      return
    }

    const contextRanges = contextRangesForMarkers({
      paths: new Set(paths),
      diffSummaries: get().diffSummaries,
    })

    // progressive diff entries are updated in the other store to avoid two zustand updates for perf
    const loadedEntries = await get().diffEntriesActions.loadDiffEntries({
      paths,
      contextRanges,
      isComparisonOutdated: getIsComparisonOutdated?.(),
    })

    for (const entry of loadedEntries) {
      digestsLoading.delete(entry.pathDigest)
      digestsLoaded.add(entry.pathDigest)
    }

    // loadManyPriority may temporarily change the chunk size (e.g. loading a whole virtual
    // window in a single batch). Always reset back to the default so that change does not
    // persist across subsequent batches.
    if (isBatchingRefinementEnabled()) {
      lengthOfItemsToLoad = DEFAULT_ITEMS_TO_LOAD
    } else if (lengthOfItemsToLoad < DEFAULT_ITEMS_TO_LOAD) {
      // loadPriority will sometimes request a single item loaded at the front of the deque
      // reset the length back to the default after that happens
      lengthOfItemsToLoad = DEFAULT_ITEMS_TO_LOAD
    }

    // do not return debouncedProcessQueue as `Maximum call stack size exceeded` will happen in tests
    debouncedProcessQueue()
    return
  }

  return {
    startQueue: (options: {autoLoadAllDiffs: boolean} = {autoLoadAllDiffs: false}) => {
      queueRunning = true
      autoLoadAllDiffs = options.autoLoadAllDiffs
      debouncedProcessQueue()
    },
    loadPriority: (pathDigest?: string) => {
      if (!pathDigest) return
      digestsToLoad.pushFront(pathDigest)
      // Debounce: reset the timer so we batch multiple rapid calls
      debouncedProcessQueue()
    },
    loadManyPriority: (pathDigests: string[]) => {
      digestsToLoad.pushFrontMany(pathDigests)
      // Load the whole requested window in a single batch. When the refinement is enabled,
      // never drop below the default; otherwise mirror the legacy behavior exactly.
      lengthOfItemsToLoad = isBatchingRefinementEnabled()
        ? Math.max(pathDigests.length, DEFAULT_ITEMS_TO_LOAD)
        : pathDigests.length
      // Debounce: reset the timer so we batch multiple rapid calls
      debouncedProcessQueue()
    },
    resume: () => {
      // With the flag off the empty-queue poll already notices state changes, so waking it here would only
      // start batches earlier than before.
      if (isSkipEmptyPollEnabled()) debouncedProcessQueue()
    },
  }
}
